import { useMemo, type ReactNode } from 'react';

import type { InventoryAction, InventoryProvider, InventoryTree } from '@scaleaq/sitecontrol-lib';

import {
  useGetAllInventory,
  useGetDistinctInventoryTypes,
  useGetInventoryNames,
} from '@/dataAccess/rest';
import { assembleInventoryForest } from '@/utils';

import { InventoryDataContext, type InventoryDataContextValue } from './inventoryDataProvider/';
import { useSiteContext } from './SiteContext.ts';

const emptyInventoryById = new Map<string, InventoryTree>();
const emptyNodes: InventoryTree[] = [];
const emptyProviders: InventoryProvider[] = [];
const emptyActions: InventoryAction[] = [];

type InventoryDataProviderProps = {
  children: ReactNode;
};

/**
 * Subscribes once per site to inventory list, distinct types, id → name map (`getNames`), and
 * derived indexes via {@link useInventoryDataCtx}. Mutations that invalidate the `inventory` query
 * key refresh all consumers (including distinct types and names).
 */
export const InventoryDataProvider = ({ children }: InventoryDataProviderProps) => {
  const { currentSite: { id: inventoryId } = {} } = useSiteContext();
  const {
    data: flatNodes,
    isLoading,
    isFetching,
    isError,
    isFetched,
    error,
  } = useGetAllInventory(inventoryId);

  const {
    data: distinctInventoryTypes,
    isLoading: isDistinctTypesLoading,
    isError: isDistinctTypesError,
  } = useGetDistinctInventoryTypes(inventoryId);

  const { data: inventoryNames, isLoading: isInventoryNamesLoading } = useGetInventoryNames();

  const forest = useMemo(
    () => (flatNodes === undefined ? undefined : assembleInventoryForest(flatNodes)),
    [flatNodes]
  );

  const roots = forest?.roots;
  const inventoryById = forest?.inventoryById ?? emptyInventoryById;
  const firstRootInventories = forest?.firstRootInventories ?? emptyNodes;
  const rootProviders = forest?.rootProviders ?? emptyProviders;
  const rootActions = forest?.rootActions ?? emptyActions;

  const inventoryNameById = useMemo<ReadonlyMap<string, string>>(
    () => new Map(Object.entries(inventoryNames ?? {})),
    [inventoryNames]
  );

  const value = useMemo<InventoryDataContextValue>(
    () => ({
      roots,
      inventoryById,
      inventoryNameById,
      isInventoryNamesLoading,
      firstRootInventories,
      rootProviders,
      rootActions,
      distinctInventoryTypes,
      isDistinctTypesLoading,
      isDistinctTypesError,
      isLoading,
      isFetching,
      isError,
      isFetched,
      error,
    }),
    [
      roots,
      inventoryById,
      inventoryNameById,
      isInventoryNamesLoading,
      firstRootInventories,
      rootProviders,
      rootActions,
      distinctInventoryTypes,
      isDistinctTypesLoading,
      isDistinctTypesError,
      isLoading,
      isFetching,
      isError,
      isFetched,
      error,
    ]
  );

  return <InventoryDataContext.Provider value={value}>{children}</InventoryDataContext.Provider>;
};
